import { useMutation, useQuery } from '@tanstack/react-query';
import { List, ListItem, Toggle } from 'framework7-react';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import dayjs from 'dayjs';
import { orderBy, uniq, update } from 'lodash';
import pluralize from 'pluralize';
import { KING_OF_DRAG_ENABLED } from 'src/constants/FeatureFlags';

import API from '@api/API';

import {
  useCurrentEntertainerQuery,
  useEntertainerDirectory,
  useFetchCurrentEntertainer,
} from '@queries/EntertainerQueries';
import { useUnauthorizedEventsQuery } from '@queries/EventQueries';
import { useFeaturedLooksFeedPostsQuery } from '@queries/PostQueries';
import { Series, useSeriesByTagSlug, useSeriesEntertainersQuery } from '@queries/TagQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useAnalytics } from '@hooks/useAnalytics';
import { CurrentCity, useCitiesQuery, useCityById } from '@hooks/useCurrentUserLocation';

import {
  EntertainerDashboardAction,
  useLocation,
  useNavigate,
  useParams,
  useRouter,
} from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import { ShareLinkButton } from '@components/buttons/ShareLinkButton';
import EventCard, {
  EventActions,
  EventHeader,
  EventImage,
  PromoterHeader,
} from '@components/cards/EventCard';
import { VenueCard } from '@components/cards/VenueCard';
import { Form } from '@components/form/Form';
import { FormLabel } from '@components/form/FormLabel';
import { ImageUploadField } from '@components/form/ImageUploadField';
import { Input } from '@components/form/Input';
import { Select } from '@components/form/Select';
import { Textarea } from '@components/form/Textarea';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import { ButtonFilter } from '@components/layout/ButtonFilter/ButtonFilter';
import Card from '@components/layout/Card/Card';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { ErrorBoundary } from '@components/layout/ErrorBoundary';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { LinkBanner } from '@components/layout/LinkBanner/LinkBanner';
import { Popup } from '@components/layout/Popup/Popup';
import { Screen } from '@components/layout/Screen/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { UserGagglRankingCard } from '@screens/HomeScreen/HomeScreen';
import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import { MultiSelectField } from '@screens/entertainer/components/CitiesField';
import { AddImageContent } from '@screens/entertainer/components/FeaturedPhotoManagementSection';
import { VenmoCard } from '@screens/entertainer/components/VenmoCard';
import { SeriesEntertainersList } from '@screens/series/SeriesScreen/SeriesScreen';

import dollarsSvgUrl from '../SeriesScreen/dollars.svg';
import magazineSvgUrl from '../SeriesScreen/magazine.svg';
import featureScreenshotImageUrl from './images/feature-example.jpg';

const useUpdateSeriesApplicationMutation = (seriesId: string) => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: (
      updatedProperties: Partial<{
        expectedCompensation?: string;
        questions: Record<
          string,
          {
            question: string;
            answer: string;
            meta?: Record<string, any>;
          }
        >;
      }>,
    ) => {
      return API.post(`/v1/series/${seriesId}/application`, updatedProperties);
    },
  });

  return {
    isUpdatingSeriesApplication: isPending,
    updateSeriesApplicationAsync: mutateAsync,
  };
};

enum UserSeriesApplicationQuestion {
  AboutEntertainer = 'aboutEntertainer',
  AboutScene = 'aboutScene',
  SpotlightOne = 'spotlightOne',
  SpotlightTwo = 'spotlightTwo',
}

interface UserSeriesApplication {
  _id: string;
  entertainerMediaId: string;
  questions: Partial<
    Record<
      UserSeriesApplicationQuestion,
      {
        question: string;
        answer: string;
        placeholder?: string;
        meta?: Record<string, any>;
      }
    >
  >;
}

const useSeriesApplicationQuery = (seriesId: string) => {
  const { data, isFetched, refetch } = useQuery({
    queryKey: ['series', seriesId, 'application'],
    queryFn: async () => {
      const response = await API.get<{ userSeriesApplication?: UserSeriesApplication }>(
        `/v1/series/${seriesId}/application`,
      );
      return response.userSeriesApplication;
    },
    enabled: Boolean(seriesId),
  });

  return {
    userSeriesApplication: data,
    isUserSeriesApplicationReady: isFetched,
    refetchUserSeriesApplication: refetch,
  };
};

interface SeriesFeatureFormScreenProps {
  tagSlug: string;
}

const StepOne = ({
  series,
  userSeriesApplication,
  onSave,
}: {
  series: Series;
  userSeriesApplication?: UserSeriesApplication;
  onSave: () => void;
}) => {
  return (
    <>
      <TitleToolbar text="Step 1: Select your cover photo" size="sm" />

      <p className="mt-0">
        We need a cover photo for your feature in the Instagram post! Select one of your favorite
        looks and give it a title.
      </p>

      <AddImageContent
        cityId={series.city._id}
        meta={{
          channel: 'series',
          channelId: series._id,
        }}
        onSave={onSave}
      />
    </>
  );
};

const StepTwo = ({
  series,
  userSeriesApplication,
  onSave,
}: {
  series: Series;
  userSeriesApplication?: UserSeriesApplication;
  onSave: () => void;
}) => {
  const { track } = useAnalytics();
  const [answers, setAnswers] = useState({
    aboutEntertainer: {
      question: 'About your drag *',
      placeholder: 'Tell us about your drag...',
      answer: userSeriesApplication?.questions?.aboutEntertainer?.answer,
    },
    aboutScene: {
      question: `What do you wish vistors knew about drag in ${series.city.name}? *`,
      placeholder: `You HAVE to see drag in ${series.city.name} because...`,
      answer: userSeriesApplication?.questions?.aboutScene?.answer,
    },
  });

  const { isUpdatingSeriesApplication, updateSeriesApplicationAsync } =
    useUpdateSeriesApplicationMutation(series._id);

  const handleContinue = async () => {
    track('Submit Series Feature Step', { step: 2 });
    await updateSeriesApplicationAsync({
      questions: answers,
    });
    onSave();
  };

  const questionFields = useMemo(() => {
    return Object.entries(answers).map(([key, { question, placeholder, answer }]) => (
      <div key={key}>
        <FormLabel text={question} />
        <Textarea
          name={key}
          placeholder={placeholder}
          defaultValue={answer}
          onChange={(updatedValue) => {
            setAnswers((prevAnswers) => ({
              ...prevAnswers,
              [key]: {
                ...prevAnswers[key],
                answer: updatedValue,
              },
            }));
          }}
        />
      </div>
    ));
  }, []);

  return (
    <>
      <TitleToolbar text="Step 2: Share your story" size="sm" />

      <p className="mt-0">
        We want to properly highlight of your drag and what makes you unique! Answer some questions
        about yourself and the local drag scene in {series.city.name}.
      </p>

      <div className="d-grid gap-2">
        {questionFields}
        <Button
          data-testid="steptwo-continue-button"
          roundness="rounded"
          disabled={
            isUpdatingSeriesApplication ||
            !answers.aboutEntertainer.answer ||
            !answers.aboutScene.answer
          }
          onClick={handleContinue}
        >
          {isUpdatingSeriesApplication ? 'Submitting...' : 'Continue'}
        </Button>
      </div>
    </>
  );
};

export const EntertainerSpotlightSection: FC<{
  label: string;
  entertainerOptions: { value: string; label: string }[];
  spotlights: { entertainerId: string; reason: string }[];
  index: number;
  name: string;
  updateSpotlightEntertainer: (index: number) => (updatedValues: string[]) => void;
  updateSpotlightEntertainerReason: (index: number) => (updatedReason: string) => void;
}> = ({
  label,
  entertainerOptions,
  spotlights,
  index,
  name,
  updateSpotlightEntertainer,
  updateSpotlightEntertainerReason,
}) => {
  const { entertainerId, reason } = spotlights[index] ?? {};
  const entertainer = entertainerOptions.find((option) => option.value === entertainerId);

  return (
    <div>
      <MultiSelectField
        label={label}
        allowSearch
        isSingleSelect
        options={entertainerOptions}
        defaultValue={entertainerId ? [entertainerId] : []}
        placeholder="Select entertainer"
        name={`${name}Entertainer`}
        onChange={updateSpotlightEntertainer(index)}
      />
      <div className="mt-1">
        <Textarea
          disabled={!entertainer}
          defaultValue={reason}
          name={`${name}Reason`}
          placeholder={
            entertainer
              ? `(e.g., "You have not LIVED until you have seen ${entertainer.label} perform live! No doubt my favorite performer in town! 🔥")`
              : ''
          }
          onChange={updateSpotlightEntertainerReason(index)}
        />
      </div>
    </div>
  );
};

const StepThree = ({
  series,
  userSeriesApplication,
  onSave,
}: {
  series: Series;
  userSeriesApplication?: UserSeriesApplication;
  onSave: () => void;
}) => {
  const { track } = useAnalytics();
  const [spotlights, setSpotlights] = useState<{ entertainerId: string; reason: string }[]>([
    {
      entertainerId: userSeriesApplication?.questions?.spotlightOne?.meta?.entertainerId ?? '',
      reason: userSeriesApplication?.questions?.spotlightOne?.meta?.reason ?? '',
    },
    {
      entertainerId: userSeriesApplication?.questions?.spotlightTwo?.meta?.entertainerId ?? '',
      reason: userSeriesApplication?.questions?.spotlightTwo?.meta?.reason ?? '',
    },
  ]);
  const { isFetched: areEntertainersReady, entertainers = [] } = useSeriesEntertainersQuery(
    series._id,
  );

  const entertainerOptions = useMemo(() => {
    return orderBy(
      entertainers.map((entertainer) => ({ value: entertainer._id, label: entertainer.title })),
      'label',
      'asc',
    );
  }, [entertainers]);

  const updateSpotlightEntertainer = (index: number) => (updatedValues: string[]) => {
    setSpotlights((prevValue) => {
      const updatedValue = [...prevValue];
      updatedValue[index].entertainerId = updatedValues[0];
      return updatedValue;
    });
  };

  const updateSpotlightEntertainerReason = (index: number) => (updatedReason: string) => {
    setSpotlights((prevValue) => {
      const updatedValue = [...prevValue];
      updatedValue[index].reason = updatedReason;
      return updatedValue;
    });
  };

  const { updateSeriesApplicationAsync } = useUpdateSeriesApplicationMutation(series._id);
  const handleSubmit = async () => {
    track('Submit Series Feature Step', { step: 3 });

    const entertainerOne = entertainerOptions.find(
      (entertainer) => entertainer.value === spotlights[0].entertainerId,
    );
    const entertainerTwo = entertainerOptions.find(
      (entertainer) => entertainer.value === spotlights[1].entertainerId,
    );

    await updateSeriesApplicationAsync({
      questions: {
        spotlightOne: {
          question: 'First entertainer spotlight',
          answer: entertainerOne.label,
          meta: {
            entertainerId: spotlights[0].entertainerId,
            reason: spotlights[0].reason,
          },
        },
        spotlightTwo: {
          question: 'Second entertainer spotlight',
          answer: entertainerTwo.label,
          meta: {
            entertainerId: spotlights[1].entertainerId,
            reason: spotlights[1].reason,
          },
        },
      },
    });

    onSave();
  };

  const hasEmptyFields =
    spotlights.length && spotlights.some(({ entertainerId, reason }) => !entertainerId || !reason);
  const isSameSpotlightEntertainer =
    spotlights?.[0]?.entertainerId &&
    spotlights?.[0]?.entertainerId === spotlights?.[1]?.entertainerId;

  return (
    <>
      <TitleToolbar text="Step 3: Endorse local artists" size="sm" />

      <p className="mt-0">
        In your feature, we'd love to highlight other local drag artists who you want to spotlight
        as well. Select two entertainers to support and give a couple reasons why!
      </p>

      {areEntertainersReady && entertainerOptions?.length ? (
        <div className="d-grid gap-2">
          <EntertainerSpotlightSection
            label="First entertainer *"
            entertainerOptions={entertainerOptions}
            spotlights={spotlights}
            index={0}
            name="spotlightOne"
            updateSpotlightEntertainer={updateSpotlightEntertainer}
            updateSpotlightEntertainerReason={updateSpotlightEntertainerReason}
          />
          {spotlights?.[0]?.entertainerId && (
            <EntertainerSpotlightSection
              label="Second entertainer *"
              entertainerOptions={entertainerOptions}
              spotlights={spotlights}
              index={1}
              name="spotlightTwo"
              updateSpotlightEntertainer={updateSpotlightEntertainer}
              updateSpotlightEntertainerReason={updateSpotlightEntertainerReason}
            />
          )}
          <Button
            data-testid="stepthree-submit-button"
            roundness="rounded"
            disabled={hasEmptyFields || isSameSpotlightEntertainer}
            onClick={handleSubmit}
          >
            Finish up!
          </Button>
          {isSameSpotlightEntertainer && (
            <p className="text-danger mt-0 text-center">
              Please select two different entertainers.
            </p>
          )}
        </div>
      ) : (
        <>
          <Skeleton height={60} />
          <Skeleton height={60} />
        </>
      )}
    </>
  );
};

const FormContent: FC<{ series: Series; userSeriesApplication?: UserSeriesApplication }> = ({
  series,
  userSeriesApplication,
}) => {
  const { track } = useAnalytics();
  const [isComplete, setIsComplete] = useState(() =>
    Boolean(
      userSeriesApplication?.entertainerMediaId &&
      userSeriesApplication?.questions?.aboutEntertainer?.answer &&
      userSeriesApplication?.questions?.aboutScene?.answer &&
      userSeriesApplication?.questions?.spotlightOne?.answer &&
      userSeriesApplication?.questions?.spotlightTwo?.answer,
    ),
  );
  const [step, setStep] = useState<1 | 2 | 3>(() => {
    if (!userSeriesApplication?.entertainerMediaId) {
      return 1;
    }

    return 2;
  });

  useEffect(() => {
    track('Show Series Feature Form Step', { step });
  }, [step]);

  if (isComplete) {
    return (
      <EmptyState
        title="Application Submitted!"
        text="Thank you for applying to be featured! We will review your application and reach out if you are selected."
      />
    );
  }

  return (
    <>
      {step === 1 && (
        <StepOne
          series={series}
          userSeriesApplication={userSeriesApplication}
          onSave={() => setStep(2)}
        />
      )}
      {step === 2 && (
        <StepTwo
          series={series}
          userSeriesApplication={userSeriesApplication}
          onSave={() => setStep(3)}
        />
      )}
      {step === 3 && (
        <StepThree
          series={series}
          userSeriesApplication={userSeriesApplication}
          onSave={() => setIsComplete(true)}
        />
      )}
    </>
  );
};

export const ContributeAndEarnScreen = createScreenComponent<{ tagSlug: string }, {}>(
  ({ hideBack, tagSlug }) => {
    const { track } = useAnalytics();
    const { back } = useRouter();

    const { isFetched: isSeriesReady, series } = useSeriesByTagSlug(tagSlug);
    const { userIsLoggedIn } = useAppContext();
    const { createAuthGateHandler } = useAuthGate();
    const { entertainer } = useCurrentEntertainerQuery();
    const backUrl = `/noauth/series/${tagSlug}?tab=postersReviews`;

    const handleStartHype = () => {
      track('Start Hype Clicked');
      createAuthGateHandler(
        () => {
          back(backUrl);
        },
        {
          entertainerCallback: () => {},
          authBeforeEntertainerSignup: true,
          cityId: series?.city?._id,
        },
      );
    };

    const handleSeeBreakdown = () => {
      track('See Breakdown Clicked');
      createAuthGateHandler(
        () => {
          back(backUrl);
        },
        {
          entertainerCallback: () => {},
          authBeforeEntertainerSignup: true,
          cityId: series?.city?._id,
        },
      );
    };

    return (
      <Screen
        name="Support And Earn Screen"
        hideToolbar
        headerProps={{
          left: hideBack ? <></> : undefined,
          back: { to: backUrl },
          title: <Screen.GagglTitle />,
        }}
        bottomToolbar={
          <Screen.BottomToolbar>
            <div className="d-flex gap-2 w-100">
              <Button roundness="rounded" onClick={handleStartHype}>
                Get Started!
              </Button>
            </div>
          </Screen.BottomToolbar>
        }
      >
        <Screen.Content>
          {isSeriesReady ? (
            series && (
              <>
                <div className="d-flex mb-3 gap-2 align-items-center">
                  <img src={dollarsSvgUrl} height={48} width={48} />
                  <div>
                    <strong className="fs-5">{series.title}</strong>
                    <br />
                    <span>Support + Earn</span>
                  </div>
                </div>

                {entertainer?._id && (
                  <div className="mb-3">
                    {entertainer.contact?.venmo ? (
                      <UserGagglRankingCard titleOverride="Your Earnings" />
                    ) : (
                      <VenmoCard entertainer={entertainer} />
                    )}
                  </div>
                )}

                <p className="mt-0">
                  <em>
                    <strong>Gaggl</strong> is here to celebrate all forms local drag especially
                    those who don't always get the spotlight — kings, things, and everything in
                    between.
                  </em>
                </p>

                <p>
                  <em>
                    <strong>
                      But this scene only works when people show up for each other. That's where you
                      come in!
                    </strong>
                  </em>
                </p>

                <hr style={{ marginTop: 36, opacity: 0.5 }} />

                <TitleToolbar text="What we need help with..." size="md" />

                <p>
                  Every show page has space for people to leave hype — shout out the cast, share the
                  vibe, or say what you're excited for.
                </p>

                <p>A few words of hype can go a long way in helping people discover a show.</p>

                <p>This isn't about going viral. It's about showing up for the community!</p>

                <p>
                  As a <strong>Trusted Supporter</strong>, you help feature the shows and performers{' '}
                  <strong>that have to work harder in the community!</strong>
                </p>

                <hr style={{ marginTop: 36, opacity: 0.5 }} />

                <TitleToolbar text="What this will look like..." size="md" />

                <p>
                  Every week we post a roundup of shows on our Instagram featuring quotes from the
                  hype left on show pages.
                </p>

                <p>
                  <em>
                    <strong>
                      If your hype gets featured, it may appear in the post like this:
                    </strong>
                  </em>
                </p>

                <p>
                  <img
                    src={featureScreenshotImageUrl}
                    style={{ maxWidth: '100%', width: 'auto', height: 'auto' }}
                  />
                </p>

                <hr style={{ marginTop: 36, opacity: 0.5 }} />

                <TitleToolbar text="How you'll be compensated..." size="md" />

                <p>
                  Each featured show has a <strong>$5 tip pool.</strong>
                </p>

                <p>
                  If your hype quote is selected for the slide, you'll share that tip pool with the
                  other Trusted Supporters whose quotes were featured.
                </p>

                <p>
                  <strong>For Example:</strong> If four quotes are featured on a show's slide, the
                  $5 tip pool is split evenly between those quotes. So if two of those quotes are
                  yours, you would receive <strong>$2.50</strong>, and the others would receive{' '}
                  <strong>$1.25 each</strong>.
                </p>

                <p>
                  Think of it as a small{' '}
                  <strong>thank-you tip for helping hype the shows you love.</strong>
                </p>

                <hr style={{ marginTop: 36, opacity: 0.5 }} />

                <TitleToolbar text="How you can track it..." size="md" />

                <p>
                  We know this is new and a little hard to wrap your head around, so we want to be
                  as transparent as possible about how the hype you leave translates into
                  compensation.
                </p>
                <p>
                  On the main screen, you'll be able to see a breakdown of the number of shows
                  you've hyped and how much you could earn for each.
                </p>

                <div>
                  <Button roundness="rounded" onClick={handleSeeBreakdown}>
                    See your breakdown
                  </Button>
                </div>
              </>
            )
          ) : (
            <>
              <Skeleton height={60} />
              <Skeleton height={60} />
              <Skeleton height={80} />
              <Skeleton height={40} />
              <Skeleton height={40} />
              <Skeleton height={40} />
            </>
          )}
        </Screen.Content>
      </Screen>
    );
  },
);
